import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';

import {
  OfflineDetailEintrag,
  OfflineKategorieEintrag,
  OfflineListeEintrag,
  OfflineMessstelleEintrag,
  OfflineMetaEintrag,
  OfflineProjektEintrag,
  OfflineQrZuordnungEintrag,
  OfflineSpsEintragEintrag,
  OfflineSpsListeEintrag,
  OfflineSpsSignalEintrag,
  SyncKonfliktEintrag,
  SyncQueueEintrag
} from '../../Models/offline.model';

/**
 * Der lokale Datenbestand für die Arbeit ohne Netz.
 *
 * Bewusst IndexedDB (über Dexie) und nicht der Cache des Service Workers:
 * hier liegen keine Antworten, sondern Fachdaten, die vor Ort GEÄNDERT
 * werden. Ein HTTP-Cache kann das nicht – er kennt nur ganze Antworten und
 * keine Prüfpunkte, keine Warteschlange und keine Änderungsstände.
 *
 * Der Service Worker macht weiterhin die Anwendung selbst offline
 * verfügbar (HTML, JavaScript, Bilder); die Trennung ist Absicht.
 *
 * Tabellen (Version 1, Messstellen):
 *
 * | Tabelle       | Inhalt                                                  |
 * |---------------|---------------------------------------------------------|
 * | `listen`      | offline verfügbare Inbetriebnahmen (Kopfdaten)          |
 * | `messstellen` | Zeilen für Listenansicht, Suche, Filter, Blättern       |
 * | `details`     | vollständige Messstellen inkl. Prüfpunkten und Verlauf  |
 * | `kategorien`  | Kategorien samt Abschnitten und Prüfpunkt-Vorlagen      |
 * | `syncQueue`   | erfasste Änderungen, die noch zum Server müssen         |
 * | `konflikte`   | Änderungen, die eine Entscheidung brauchen              |
 * | `meta`        | Download- und Sync-Metadaten                            |
 *
 * Tabellen (Version 2, Projekt):
 *
 * | Tabelle          | Inhalt                                                |
 * |------------------|-------------------------------------------------------|
 * | `projekte`       | offline verfügbare Projekte (Kopfdaten + Projektseite)|
 * | `spsListen`      | SPS-Listen (Zuordnungslisten, PLC-Tags)               |
 * | `spsEintraege`   | Originalzeilen der SPS-Listen                         |
 * | `spsSignale`     | SPS-Signale samt Quellen und Prüfverlauf              |
 * | `qrZuordnungen`  | aktive QR-Zuordnung je Messstelle                     |
 *
 * WICHTIG: Diese Datenbank wird beim Abmelden NICHT geleert. Ein abgelaufener
 * Token oder ein versehentlicher Logout darf einen Tag Arbeit auf der Anlage
 * nicht vernichten. Entfernt wird ein Bestand nur, wenn der Benutzer es
 * ausdrücklich verlangt – und auch dann nur, wenn nichts mehr aussteht.
 */
export class ContainuelOfflineDb extends Dexie {
  listen!: Table<OfflineListeEintrag, number>;
  messstellen!: Table<OfflineMessstelleEintrag, number>;
  details!: Table<OfflineDetailEintrag, number>;
  kategorien!: Table<OfflineKategorieEintrag, number>;
  syncQueue!: Table<SyncQueueEintrag, string>;
  konflikte!: Table<SyncKonfliktEintrag, string>;
  meta!: Table<OfflineMetaEintrag, string>;

  projekte!: Table<OfflineProjektEintrag, number>;
  spsListen!: Table<OfflineSpsListeEintrag, number>;
  spsEintraege!: Table<OfflineSpsEintragEintrag, number>;
  spsSignale!: Table<OfflineSpsSignalEintrag, number>;
  qrZuordnungen!: Table<OfflineQrZuordnungEintrag, number>;

  constructor() {
    super('ContainuelInbetriebnahmeOffline');

    this.version(1).stores({
      listen: 'id, downloadedAtUtc, projektId',

      // Der zusammengesetzte Index trägt die Listenansicht: alle Messstellen
      // einer Inbetriebnahme in Zeilenreihenfolge, ohne Sortieren im Speicher.
      messstellen: 'id, messstellenListeId, [messstellenListeId+rowNumber]',

      details: 'id, messstellenListeId',

      kategorien: 'id',

      // `createdAtUtc` ist die Abarbeitungsreihenfolge: ein offline angelegter
      // Prüfpunkt muss vor der Änderung seines Ergebnisses beim Server sein.
      syncQueue: 'id, messstellenListeId, createdAtUtc, typ',

      konflikte: 'id, messstellenListeId, erkanntAmUtc',

      meta: 'key'
    });

    /*
     * Version 2: das Projekt als Offline-Einheit.
     *
     * Rein additiv – die Tabellen aus Version 1 behalten Schlüssel und Daten,
     * sie bekommen nur den Index `projektId`. Dexie übernimmt vorhandene
     * Zeilen unverändert; der `upgrade`-Schritt trägt lediglich das Projekt
     * in alte Warteschlangen- und Konflikteinträge nach, soweit es sich aus
     * der Liste ergibt. Ausstehende Änderungen gehen dabei NICHT verloren –
     * sie werden nur ergänzt.
     */
    this.version(2).stores({
      listen: 'id, downloadedAtUtc, projektId',
      messstellen: 'id, messstellenListeId, [messstellenListeId+rowNumber]',
      details: 'id, messstellenListeId',
      kategorien: 'id',
      syncQueue: 'id, messstellenListeId, createdAtUtc, typ, projektId',
      konflikte: 'id, messstellenListeId, erkanntAmUtc, projektId',
      meta: 'key',

      projekte: 'id, downloadedAtUtc',
      spsListen: 'id, projektId',
      spsEintraege: 'id, listeId, projektId, [listeId+id]',
      spsSignale: 'id, projektId',

      // Der Token-Index ist die lokale Doppelvergabe-Sperre.
      qrZuordnungen: 'messstelleId, token, messstellenListeId, projektId'
    }).upgrade(async tx => {
      const listen = await tx.table('listen').toArray() as OfflineListeEintrag[];
      const projektVonListe = new Map<number, number | null>(
        listen.map(l => [l.id, l.projektId ?? null])
      );

      await tx.table('syncQueue').toCollection().modify((e: SyncQueueEintrag) => {
        if (e.projektId == null && e.messstellenListeId) {
          e.projektId = projektVonListe.get(e.messstellenListeId) ?? null;
        }
      });

      await tx.table('konflikte').toCollection().modify((k: SyncKonfliktEintrag) => {
        if (k.projektId == null && k.messstellenListeId) {
          k.projektId = projektVonListe.get(k.messstellenListeId) ?? null;
        }

        if (!k.art) {
          k.art = 'FELD';
        }

        if (!k.felder) {
          k.felder = [];
        }
      });

      /*
       * QR-Tokens, die ein älteres Listenpaket noch nicht kannte, fehlen
       * hier schlicht; sie kommen mit dem nächsten Download. Nichts wird
       * dafür verworfen.
       */
    });
  }
}

/**
 * Zugriff auf den lokalen Bestand.
 *
 * Alles, was Daten UND Warteschlange zugleich anfasst, läuft hier durch eine
 * Dexie-Transaktion. Das ist keine Förmlichkeit: bräche der Vorgang zwischen
 * beidem ab, stünde die Änderung in der Oberfläche, ginge aber nie zum Server
 * – oder umgekehrt.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDbService {
  readonly db = new ContainuelOfflineDb();

  /**
   * Ist IndexedDB überhaupt benutzbar? In einem privaten Fenster oder bei
   * gesperrtem Speicher kann der Zugriff scheitern. Dann arbeitet die
   * Anwendung wie bisher rein online weiter, statt mit Fehlern um sich zu
   * werfen.
   */
  private verfuegbarkeit: Promise<boolean> | null = null;

  istVerfuegbar(): Promise<boolean> {
    if (!this.verfuegbarkeit) {
      this.verfuegbarkeit = this.db
        .open()
        .then(() => true)
        .catch(fehler => {
          console.error('Der lokale Offline-Speicher konnte nicht geöffnet werden.', fehler);
          return false;
        });
    }

    return this.verfuegbarkeit;
  }

  // ── Listen ──

  async ladeListen(): Promise<OfflineListeEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.listen.toArray();
  }

  async ladeListe(id: number): Promise<OfflineListeEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.listen.get(id);
  }

  async istOfflineVerfuegbar(id: number): Promise<boolean> {
    return !!(await this.ladeListe(id));
  }

  /** Die lokal vorhandenen Listen eines Projekts. */
  async ladeListenVonProjekt(projektId: number): Promise<OfflineListeEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.listen.where('projektId').equals(projektId).toArray();
  }

  // ── Messstellen ──

  async ladeMessstellen(listeId: number): Promise<OfflineMessstelleEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.messstellen
      .where('[messstellenListeId+rowNumber]')
      .between([listeId, Dexie.minKey], [listeId, Dexie.maxKey])
      .toArray();
  }

  async ladeDetail(messstelleId: number): Promise<OfflineDetailEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.details.get(messstelleId);
  }

  // ── Kategorien ──

  async ladeKategorien(): Promise<OfflineKategorieEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.kategorien.orderBy('id').toArray();
  }

  // ── Projekte ──

  async ladeProjekte(): Promise<OfflineProjektEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.projekte.toArray();
  }

  async ladeProjekt(id: number): Promise<OfflineProjektEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.projekte.get(id);
  }

  async istProjektOfflineVerfuegbar(id: number): Promise<boolean> {
    return !!(await this.ladeProjekt(id));
  }

  // ── SPS ──

  async ladeSpsListen(projektId: number): Promise<OfflineSpsListeEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.spsListen.where('projektId').equals(projektId).toArray();
  }

  async ladeSpsListe(listeId: number): Promise<OfflineSpsListeEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.spsListen.get(listeId);
  }

  async ladeSpsEintraege(listeId: number): Promise<OfflineSpsEintragEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.spsEintraege.where('listeId').equals(listeId).toArray();
  }

  async ladeSpsSignale(projektId: number): Promise<OfflineSpsSignalEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.spsSignale.where('projektId').equals(projektId).toArray();
  }

  async ladeSpsSignal(signalId: number): Promise<OfflineSpsSignalEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.spsSignale.get(signalId);
  }

  // ── QR ──

  async ladeQrZuordnung(messstelleId: number): Promise<OfflineQrZuordnungEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.qrZuordnungen.get(messstelleId);
  }

  async ladeQrZuordnungNachToken(token: string): Promise<OfflineQrZuordnungEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.qrZuordnungen.where('token').equals(token).first();
  }

  // ── Warteschlange ──

  /** Alle ausstehenden Änderungen, in Erfassungsreihenfolge. */
  async ladeWarteschlange(listeId?: number): Promise<SyncQueueEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    const alle = await this.db.syncQueue.orderBy('createdAtUtc').toArray();

    return listeId == null
      ? alle
      : alle.filter(e => e.messstellenListeId === listeId);
  }

  async zaehleAusstehend(listeId?: number): Promise<number> {
    if (!(await this.istVerfuegbar())) {
      return 0;
    }

    return listeId == null
      ? this.db.syncQueue.count()
      : this.db.syncQueue.where('messstellenListeId').equals(listeId).count();
  }

  async ladeKonflikte(listeId?: number): Promise<SyncKonfliktEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return listeId == null
      ? this.db.konflikte.orderBy('erkanntAmUtc').toArray()
      : this.db.konflikte.where('messstellenListeId').equals(listeId).toArray();
  }

  async zaehleKonflikte(listeId?: number): Promise<number> {
    if (!(await this.istVerfuegbar())) {
      return 0;
    }

    return listeId == null
      ? this.db.konflikte.count()
      : this.db.konflikte.where('messstellenListeId').equals(listeId).count();
  }

  /**
   * Gehört eine Änderung oder ein Konflikt zu diesem Projekt?
   *
   * Über `projektId`, oder – bei Einträgen aus der Zeit vor Version 2 –
   * über die Liste. So zählt auch Altbestand richtig.
   */
  gehoertZuProjekt(
    eintrag: { projektId?: number | null; messstellenListeId: number },
    projektId: number,
    listenIds: ReadonlySet<number>
  ): boolean {
    if (eintrag.projektId != null) {
      return eintrag.projektId === projektId;
    }

    return listenIds.has(eintrag.messstellenListeId);
  }

  // ── Metadaten ──

  async lies<T>(key: string, standard: T): Promise<T> {
    if (!(await this.istVerfuegbar())) {
      return standard;
    }

    const eintrag = await this.db.meta.get(key);

    return eintrag ? (eintrag.value as T) : standard;
  }

  async schreibe(key: string, value: any): Promise<void> {
    if (!(await this.istVerfuegbar())) {
      return;
    }

    await this.db.meta.put({ key, value });
  }
}
